/**
 * User Interaction Utilities
 *
 * Utilities for delaying code execution until user interaction.
 * Useful for performance optimization - defer non-critical code.
 */
/**
 * Default user action events to listen for
 */
const USER_ACTION_EVENTS = ["mousedown", "mousemove", "keydown", "touchstart", "scroll"];
/**
 * Load methods after user interaction
 *
 * Delays execution of functions until the user interacts with the page.
 * Useful for deferring non-critical code to improve initial page load performance.
 *
 * @param methods - Array of functions to execute
 * @param actions - Array of event names to listen for (defaults to USER_ACTION_EVENTS)
 * @param eventCondition - Optional condition function to check before executing
 *
 * @example
 * // Execute after any user interaction
 * loadMethodOnUserAction([
 *   () => console.log('User interacted!'),
 *   () => initHeavyComponent()
 * ]);
 *
 * @example
 * // Execute only on scroll
 * loadMethodOnUserAction(
 *   [() => loadBackgroundImage()],
 *   ['scroll']
 * );
 *
 * @example
 * // Execute with condition
 * loadMethodOnUserAction(
 *   [() => showPopup()],
 *   ['scroll'],
 *   (event) => window.scrollY > 500
 * );
 */
export function loadMethodOnUserAction(methods, actions = USER_ACTION_EVENTS, eventCondition) {
    let methodIsCalled = false;
    const callMethod = (actionEvent) => {
        // Skip if already called or condition not met
        if (methodIsCalled || (eventCondition && !eventCondition(actionEvent))) {
            return;
        }
        // Execute all methods
        methods.forEach((method) => {
            setTimeout(() => {
                method();
            }, 0);
        });
        // Mark as called and remove event listeners
        methodIsCalled = true;
        actions.forEach((event) => {
            document.removeEventListener(event, callMethod);
        });
    };
    // Attach event listeners
    actions.forEach((event) => {
        document.addEventListener(event, callMethod, { passive: true });
    });
}
/**
 * Execute a function once after user scrolls
 */
export function onceUserScrolls(callback) {
    loadMethodOnUserAction([callback], ["scroll"]);
}
/**
 * Execute a function once after user touches/clicks
 */
export function onceUserInteracts(callback) {
    loadMethodOnUserAction([callback], ["mousedown", "touchstart", "scroll"]);
}
